<template>
  <div class="c-cabinet-template-info">
    <div class="template-header">
      <div class="header-left">
        <c-form-items :items="TemplateBasicInfoFormItems" :model="dataModel" :showOperation="false" />
      </div>
      <div class="header-right">
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">保存</el-button>
      </div>
    </div>

    <div class="template-content">
      <!-- 左侧可用组件 -->
      <div class="available-components">
        <div class="section-title">可用组件</div>
        <div
          v-for="component in availableComponents"
          :key="component.type"
          class="component-item"
          :draggable="true"
          @dragstart="handleDragStart($event, component)"
        >
          <div class="component-name">{{ component.name }}</div>
        </div>
      </div>

      <!-- 中间预览区域 -->
      <div class="preview-area">
        <div class="section-title">模板预览</div>
        <div
          class="preview-container"
          :style="{
            width: '850px',
            height: '678px',
            backgroundImage: dataModel.backgroundImage ? `url(${dataModel.backgroundImage})` : 'none',
            backgroundColor: dataModel.backgroundImage ? 'transparent' : '#f0f0f0'
          }"
          @drop="handleDrop"
          @dragover.prevent
          @click="handlePreviewClick"
        >
          <!-- 已添加的组件 -->
          <div
            v-for="(item, index) in previewComponents"
            :key="index"
            class="preview-component"
            :class="{ active: selectedComponentIndex === index }"
            :style="{
              position: 'absolute',
              left: item.x + 'px',
              top: item.y + 'px',
              width: item.type === 'salePrice' || item.type === 'linePrice' ? 'auto' : item.width + 'px',
              height: item.type === 'salePrice' || item.type === 'linePrice' ? 'auto' : item.height + 'px',
              fontSize: item.fontSize + 'px',
              color: item.color,
              cursor: 'move',
              transform: item.rotate ? `rotate(${item.rotate}deg)` : 'none',
              transformOrigin: 'left top'
            }"
            @click.stop="selectComponent(index)"
            @mousedown.stop="handleComponentMouseDown($event, index)"
          >
            <!-- 销售价 -->
            <div
              v-if="item.type === 'salePrice'"
              class="component-content"
              :style="`font-weight: ${item.fontWeight || 'normal'}`"
            >
              {{ item.value || '99.99' }}
            </div>
            <!-- 划线价 -->
            <div
              v-else-if="item.type === 'linePrice'"
              class="component-content line-price"
              :style="`font-weight: ${item.fontWeight || 'normal'}`"
            >
              {{ item.value || '99.99' }}
            </div>
            <!-- 二维码 -->
            <div v-else-if="item.type === 'qrCode'" class="component-content qr-code-content">
              <div class="qr-code-placeholder">
                <img src="/images/qrcode.svg" alt="二维码" />
              </div>
            </div>

            <!-- 选中时的控制点和删除按钮 -->
            <div v-if="selectedComponentIndex === index" class="component-controls">
              <!-- 只有二维码组件显示调整大小的控制点 -->
              <div v-if="item.type === 'qrCode'" class="resize-handles">
                <div
                  class="resize-handle resize-handle-nw"
                  @mousedown.stop="handleResizeStart($event, index, 'nw')"
                ></div>
                <div
                  class="resize-handle resize-handle-ne"
                  @mousedown.stop="handleResizeStart($event, index, 'ne')"
                ></div>
                <div
                  class="resize-handle resize-handle-sw"
                  @mousedown.stop="handleResizeStart($event, index, 'sw')"
                ></div>
                <div
                  class="resize-handle resize-handle-se"
                  @mousedown.stop="handleResizeStart($event, index, 'se')"
                ></div>
              </div>
              <div class="delete-button" @click.stop="handleDeleteComponent(index)" title="删除">
                <el-icon><Delete /></el-icon>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧组件属性 -->
      <div class="component-properties">
        <div class="section-title">组件属性</div>
        <div v-if="selectedComponent === null" class="empty-properties">选择一个组件进行编辑</div>
        <div v-else class="properties-content">
          <!-- 删除按钮 -->
          <div class="property-item delete-item">
            <el-button type="danger" @click="handleDeleteComponent(selectedComponentIndex)" :icon="Delete">
              删除组件
            </el-button>
          </div>

          <!-- 销售价/划线价的属性 -->
          <template v-if="selectedComponent.type === 'salePrice' || selectedComponent.type === 'linePrice'">
            <div class="property-item">
              <label>字体大小</label>
              <el-input-number v-model="selectedComponent.fontSize" :min="12" :max="100" />
            </div>
            <div class="property-item">
              <label>字体加粗</label>
              <el-select v-model="selectedComponent.fontWeight" placeholder="请选择">
                <el-option label="正常" value="normal" />
                <el-option label="加粗" value="bolder" />
              </el-select>
            </div>
            <div class="property-item">
              <label>颜色</label>
              <el-color-picker v-model="selectedComponent.color" />
            </div>
            <div class="property-item">
              <label>旋转角度</label>
              <el-input-number v-model="selectedComponent.rotate" :min="-360" :max="360" :step="1" />
            </div>
            <div class="property-item">
              <label>X坐标</label>
              <el-input-number v-model="selectedComponent.x" :min="0" :max="850" :precision="0" />
            </div>
            <div class="property-item">
              <label>Y坐标</label>
              <el-input-number v-model="selectedComponent.y" :min="0" :max="678" :precision="0" />
            </div>
            <!-- 文本组件的宽度和高度不显示，自动适应内容 -->
          </template>

          <!-- 二维码的属性 -->
          <template v-else-if="selectedComponent.type === 'qrCode'">
            <div class="property-item">
              <label>旋转角度</label>
              <el-input-number v-model="selectedComponent.rotate" :min="-360" :max="360" :step="1" />
            </div>
            <div class="property-item">
              <label>X坐标</label>
              <el-input-number v-model="selectedComponent.x" :min="0" :max="850" :precision="0" />
            </div>
            <div class="property-item">
              <label>Y坐标</label>
              <el-input-number v-model="selectedComponent.y" :min="0" :max="678" :precision="0" />
            </div>
            <div class="property-item">
              <label>宽度</label>
              <el-input-number
                v-model="selectedComponent.width"
                :min="50"
                :max="850"
                @change="handleQrCodeWidthChange"
              />
            </div>
            <div class="property-item">
              <label>高度</label>
              <el-input-number
                v-model="selectedComponent.height"
                :min="50"
                :max="678"
                @change="handleQrCodeHeightChange"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default { inheritAttrs: false }
</script>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from '@zto/zpage'
import { useCurrentAppInstance, useApiRequest } from '@zto/zpage'
import { Message } from '@zto/zpage-ui-element'
import { Delete } from '@element-plus/icons'
import { TemplateBasicInfoFormItems } from './config'

const app = useCurrentAppInstance()
const router = app.router
const route = app.router.currentRoute.value

const type: any = route.query?.type || undefined
const templateCode: any = route.query?.templateCode || route.params?.templateCode || undefined

const apiRequest = useApiRequest()

// 数据模型
const dataModel = ref<any>({
  templateName: '',
  backgroundImage: ''
})

// 可用组件列表
const availableComponents = [
  {
    type: 'salePrice',
    name: '销售价'
  },
  {
    type: 'linePrice',
    name: '划线价'
  },
  {
    type: 'qrCode',
    name: '二维码'
  }
]

// 预览区域的组件列表
interface PreviewComponent {
  type: string
  x: number
  y: number
  width: number
  height: number
  rotate?: number // 旋转角度
  fontSize?: number
  color?: string
  value?: string
  fontWeight?: string // 字体加粗
  text?: string // 文本内容
  isLine?: boolean // 是否划线
  url?: string // 二维码链接
}

const previewComponents = ref<PreviewComponent[]>([])

// 当前选中的组件索引
const selectedComponentIndex = ref<number | null>(null)

// 当前选中的组件（计算属性）
const selectedComponent = computed(() => {
  if (selectedComponentIndex.value === null) return null
  return previewComponents.value[selectedComponentIndex.value] || null
})

// 拖拽相关
let draggedComponent: any = null

// 组件移动相关
let isDragging = false
let dragStartX = 0
let dragStartY = 0
let dragComponentIndex: number | null = null

// 调整大小相关
let isResizing = false
let resizeStartX = 0
let resizeStartY = 0
let resizeStartWidth = 0
let resizeStartHeight = 0
let resizeStartLeft = 0
let resizeStartTop = 0
let resizeDirection = ''

// 处理拖拽开始
const handleDragStart = (event: DragEvent, component: any) => {
  draggedComponent = component
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'copy'
  }
}

// 处理拖拽放置
const handleDrop = (event: DragEvent) => {
  event.preventDefault()
  if (!draggedComponent) return

  const previewContainer = event.currentTarget as HTMLElement
  const rect = previewContainer.getBoundingClientRect()
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top

  // 创建新组件
  const newComponent: PreviewComponent = {
    type: draggedComponent.type,
    x: Math.round(Math.max(0, Math.min(x, 850 - 100))),
    y: Math.round(Math.max(0, Math.min(y, 678 - 50))),
    // 文本组件不需要设置 width 和 height，会自动适应内容
    width: draggedComponent.type === 'qrCode' ? 150 : 0,
    height: draggedComponent.type === 'qrCode' ? 150 : 0,
    rotate: 0,
    fontSize: 20,
    fontWeight: 'normal',
    color: '#000000',
    value: '99.99'
  }

  previewComponents.value.push(newComponent)
  selectedComponentIndex.value = previewComponents.value.length - 1
  draggedComponent = null
}

// 处理预览区域点击
const handlePreviewClick = (event: MouseEvent) => {
  // 点击空白区域，取消选中
  if ((event.target as HTMLElement).classList.contains('preview-container')) {
    selectedComponentIndex.value = null
  }
}

// 选择组件
const selectComponent = (index: number) => {
  selectedComponentIndex.value = index
}

// 处理组件鼠标按下（用于移动）
const handleComponentMouseDown = (event: MouseEvent, index: number) => {
  if ((event.target as HTMLElement).classList.contains('resize-handle')) {
    return // 如果是调整大小的控制点，不处理移动
  }

  isDragging = true
  const component = previewComponents.value[index]
  const previewContainer = (event.currentTarget as HTMLElement).closest('.preview-container') as HTMLElement
  if (previewContainer) {
    const rect = previewContainer.getBoundingClientRect()
    dragStartX = event.clientX - rect.left - component.x
    dragStartY = event.clientY - rect.top - component.y
  } else {
    dragStartX = event.clientX
    dragStartY = event.clientY
  }
  dragComponentIndex = index
  selectedComponentIndex.value = index

  document.addEventListener('mousemove', handleComponentMouseMove)
  document.addEventListener('mouseup', handleComponentMouseUp)
  event.preventDefault()
  event.stopPropagation()
}

// 处理组件鼠标移动
const handleComponentMouseMove = (event: MouseEvent) => {
  if (!isDragging || dragComponentIndex === null) return

  const previewContainer = document.querySelector('.preview-container') as HTMLElement
  if (!previewContainer) return

  const rect = previewContainer.getBoundingClientRect()
  const component = previewComponents.value[dragComponentIndex]
  // 文本组件没有固定宽度和高度，只需要确保坐标不为负即可
  const maxX = component.type === 'salePrice' || component.type === 'linePrice' ? 850 : 850 - (component.width || 0)
  const maxY = component.type === 'salePrice' || component.type === 'linePrice' ? 678 : 678 - (component.height || 0)

  const newX = Math.round(Math.max(0, Math.min(event.clientX - rect.left - dragStartX, maxX)))
  const newY = Math.round(Math.max(0, Math.min(event.clientY - rect.top - dragStartY, maxY)))

  previewComponents.value[dragComponentIndex].x = newX
  previewComponents.value[dragComponentIndex].y = newY
}

// 处理组件鼠标抬起
const handleComponentMouseUp = () => {
  isDragging = false
  dragComponentIndex = null
  document.removeEventListener('mousemove', handleComponentMouseMove)
  document.removeEventListener('mouseup', handleComponentMouseUp)
}

// 处理调整大小开始
const handleResizeStart = (event: MouseEvent, index: number, direction: string) => {
  isResizing = true
  const previewContainer = document.querySelector('.preview-container') as HTMLElement
  if (previewContainer) {
    const rect = previewContainer.getBoundingClientRect()
    resizeStartX = event.clientX
    resizeStartY = event.clientY
  } else {
    resizeStartX = event.clientX
    resizeStartY = event.clientY
  }
  resizeDirection = direction

  const component = previewComponents.value[index]
  resizeStartWidth = component.width
  resizeStartHeight = component.height
  resizeStartLeft = component.x
  resizeStartTop = component.y

  document.addEventListener('mousemove', handleResizeMove)
  document.addEventListener('mouseup', handleResizeEnd)
  event.preventDefault()
  event.stopPropagation()
}

// 处理调整大小移动
const handleResizeMove = (event: MouseEvent) => {
  if (!isResizing || selectedComponentIndex.value === null) return

  const previewContainer = document.querySelector('.preview-container') as HTMLElement
  if (!previewContainer) return

  const rect = previewContainer.getBoundingClientRect()
  const mouseX = event.clientX - rect.left
  const mouseY = event.clientY - rect.top

  const deltaX = mouseX - (resizeStartX - rect.left)
  const deltaY = mouseY - (resizeStartY - rect.top)
  const component = previewComponents.value[selectedComponentIndex.value]

  let newWidth = resizeStartWidth
  let newHeight = resizeStartHeight
  let newX = resizeStartLeft
  let newY = resizeStartTop

  if (resizeDirection.includes('e')) {
    newWidth = Math.max(50, Math.min(resizeStartWidth + deltaX, 850 - resizeStartLeft))
  }
  if (resizeDirection.includes('w')) {
    const minWidth = 50
    const maxWidth = resizeStartLeft + resizeStartWidth
    newWidth = Math.max(minWidth, Math.min(resizeStartWidth - deltaX, maxWidth))
    newX = Math.max(0, Math.min(resizeStartLeft + deltaX, resizeStartLeft + resizeStartWidth - minWidth))
  }
  if (resizeDirection.includes('s')) {
    newHeight = Math.max(
      component.type === 'qrCode' ? 50 : 20,
      Math.min(resizeStartHeight + deltaY, 678 - resizeStartTop)
    )
  }
  if (resizeDirection.includes('n')) {
    const minHeight = component.type === 'qrCode' ? 50 : 20
    const maxHeight = resizeStartTop + resizeStartHeight
    newHeight = Math.max(minHeight, Math.min(resizeStartHeight - deltaY, maxHeight))
    newY = Math.max(0, Math.min(resizeStartTop + deltaY, resizeStartTop + resizeStartHeight - minHeight))
  }

  // 如果是二维码组件，保持 1:1 比例
  if (component.type === 'qrCode') {
    // 计算宽度和高度的变化量
    const widthDelta = Math.abs(newWidth - resizeStartWidth)
    const heightDelta = Math.abs(newHeight - resizeStartHeight)

    // 使用变化量较大的那个方向来确定最终尺寸
    if (widthDelta > heightDelta) {
      // 宽度变化更大，高度跟随宽度
      newHeight = newWidth
    } else {
      // 高度变化更大，宽度跟随高度
      newWidth = newHeight
    }
  }

  // 确保不超出边界
  if (newX + newWidth > 850) {
    newWidth = 850 - newX
    // 如果是二维码，需要同时调整高度
    if (component.type === 'qrCode') {
      newHeight = newWidth
    }
  }
  if (newY + newHeight > 678) {
    newHeight = 678 - newY
    // 如果是二维码，需要同时调整宽度
    if (component.type === 'qrCode') {
      newWidth = newHeight
    }
  }
  if (newX < 0) {
    newWidth += newX
    newX = 0
    // 如果是二维码，需要同时调整高度
    if (component.type === 'qrCode') {
      newHeight = newWidth
    }
  }
  if (newY < 0) {
    newHeight += newY
    newY = 0
    // 如果是二维码，需要同时调整宽度
    if (component.type === 'qrCode') {
      newWidth = newHeight
    }
  }

  component.width = Math.max(component.type === 'qrCode' ? 50 : 50, newWidth)
  component.height = Math.max(component.type === 'qrCode' ? 50 : 20, newHeight)
  component.x = Math.round(newX)
  component.y = Math.round(newY)
}

// 处理调整大小结束
const handleResizeEnd = () => {
  isResizing = false
  resizeDirection = ''
  document.removeEventListener('mousemove', handleResizeMove)
  document.removeEventListener('mouseup', handleResizeEnd)
}

// 处理二维码宽度变化（保持 1:1 比例）
const handleQrCodeWidthChange = (val: number) => {
  if (selectedComponentIndex.value !== null) {
    const component = previewComponents.value[selectedComponentIndex.value]
    if (component.type === 'qrCode') {
      component.height = val
    }
  }
}

// 处理二维码高度变化（保持 1:1 比例）
const handleQrCodeHeightChange = (val: number) => {
  if (selectedComponentIndex.value !== null) {
    const component = previewComponents.value[selectedComponentIndex.value]
    if (component.type === 'qrCode') {
      component.width = val
    }
  }
}

// 保存状态
const saving = ref(false)

// 保存
const handleSave = async () => {
  if (!dataModel.value.templateName?.trim()) {
    Message.warning('请输入模板名称')
    return
  }
  if (!dataModel.value.backgroundImage) {
    Message.warning('请上传背景图片')
    return
  }

  saving.value = true
  try {
    let action = ''
    switch (type) {
      case 'add':
        action = '/cabinet/admin/mkt/AdminMktProductTemplateCommandController/create'
        break

      case 'edit':
        action = '/cabinet/admin/mkt/AdminMktProductTemplateCommandController/edit'
        break

      default:
        break
    }

    if (!previewComponents.value?.length) {
      Message.warning('请至少添加一个组件')
      saving.value = false
      return
    }

    // 校验：至少需要一个二维码组件
    const hasQrCode = previewComponents.value.some(comp => comp.type === 'qrCode')
    if (!hasQrCode) {
      Message.warning('请至少添加一个二维码组件')
      saving.value = false
      return
    }

    // 构建符合后端要求的数据结构
    const components = previewComponents.value.map(comp => {
      const baseComponent: any = {
        type: comp.type === 'salePrice' || comp.type === 'linePrice' ? 'text' : comp.type,
        rotate: Math.round(comp.rotate || 0),
        x: Math.round(comp.x),
        y: Math.round(comp.y)
      }

      // 文本组件属性（不包含 width 和 height，自动适应内容）
      if (comp.type === 'salePrice' || comp.type === 'linePrice') {
        baseComponent.fontSize = comp.fontSize || 16
        baseComponent.fontWeight = comp.fontWeight || 'normal'
        baseComponent.color = comp.color || '#333333'
        // salePrice组件保存时text: '$salePrice'，linePrice组件保存时text: '$linePrice'
        baseComponent.text = comp.type === 'salePrice' ? '$salePrice' : '$linePrice'
        if (comp.type === 'linePrice') {
          baseComponent.isLine = true
        }
      } else {
        // 二维码组件需要 width 和 height
        baseComponent.width = Math.round(comp.width)
        baseComponent.height = Math.round(comp.height)
        // qrCode组件保存时url: '$saleProductQr'
        baseComponent.url = '$saleProductQr'
      }

      return baseComponent
    })

    // 构建完整的 templateContent 对象
    const templateContentData = {
      templateCode: '$templateCode', // 保存时使用固定值 $templateCode
      backgroundSizeWidth: 850,
      backgroundSizeHeight: 678,
      backgroundUrl: dataModel.value.backgroundImage || '',
      components
    }

    // 使用 JSON.stringify 序列化
    const templateContent = JSON.stringify(templateContentData)

    const params: any = {
      templateName: dataModel.value.templateName,
      templateType: 'HP_850_678',
      backgroundImageUrl: dataModel.value.backgroundImage,
      templateContent
    }

    // 编辑时需要传递 templateCode
    if (type === 'edit' && templateCode) {
      params.templateCode = templateCode
    }

    console.log('params===', params)

    await apiRequest({
      action,
      params
    })

    Message.success('保存成功！')
    router.close()
  } catch (error) {
    console.error('保存失败:', error)
  } finally {
    saving.value = false
  }
}

// 删除组件
const handleDeleteComponent = (index: number | null) => {
  if (index === null) return

  previewComponents.value.splice(index, 1)
  selectedComponentIndex.value = null
}

// 取消
const handleCancel = () => {
  router.close()
}

// 加载模板详情数据（编辑模式）
const loadTemplateDetail = async () => {
  if (type !== 'edit' || !templateCode) return

  try {
    const res = await apiRequest({
      action: '/cabinet/admin/mkt/AdminMktProductTemplateQueryController/detail',
      params: { templateCode }
    })

    // 回显基本信息
    if (res.templateName) {
      dataModel.value.templateName = res.templateName
    }
    if (res.backgroundImageUrl) {
      dataModel.value.backgroundImage = res.backgroundImageUrl
    }

    // 解析 templateContent
    if (res.templateContent) {
      try {
        const templateContentData =
          typeof res.templateContent === 'string' ? JSON.parse(res.templateContent) : res.templateContent

        // 回显组件列表
        if (templateContentData.components && Array.isArray(templateContentData.components)) {
          previewComponents.value = templateContentData.components.map((comp: any) => {
            const previewComp: PreviewComponent = {
              type: comp.type === 'text' ? (comp.isLine ? 'linePrice' : 'salePrice') : comp.type,
              x: Math.round(comp.x || 0),
              y: Math.round(comp.y || 0),
              width: comp.width ? Math.round(comp.width) : 0,
              height: comp.height ? Math.round(comp.height) : 0,
              rotate: comp.rotate ? Math.round(comp.rotate) : 0
            }

            // 文本组件属性
            if (comp.type === 'text') {
              previewComp.fontSize = comp.fontSize || 16
              previewComp.fontWeight = comp.fontWeight || 'normal'
              previewComp.color = comp.color || '#333333'
              // 从 text 字段提取 value（如果是 $salePrice 或 $linePrice，则使用默认值）
              if (comp.text && comp.text.startsWith('$')) {
                previewComp.value = '99.99'
              } else if (comp.text) {
                // 提取价格数字
                const match = comp.text.match(/[\d.]+/)
                previewComp.value = match ? match[0] : '99.99'
              } else {
                previewComp.value = '99.99'
              }
            }

            // 二维码组件属性
            if (comp.type === 'qrCode') {
              // url 不需要回显，保存时会自动设置为 '$saleProductQr'
            }

            return previewComp
          })
        }
      } catch (error) {
        console.error('解析 templateContent 失败:', error)
        Message.error('模板数据解析失败')
      }
    }
  } catch (error) {
    console.error('加载模板详情失败:', error)
    Message.error('加载模板详情失败')
  }
}

// 初始化数据
onMounted(() => {
  loadTemplateDetail()
})

// 清理事件监听
onUnmounted(() => {
  document.removeEventListener('mousemove', handleComponentMouseMove)
  document.removeEventListener('mouseup', handleComponentMouseUp)
  document.removeEventListener('mousemove', handleResizeMove)
  document.removeEventListener('mouseup', handleResizeEnd)
})
</script>

<style lang="scss" scoped>
.c-cabinet-template-info {
  padding: 20px;
  background: #fff;

  .template-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 40px;
    margin-bottom: 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid #e4e7ed;

    .header-left {
      display: flex;
      gap: 40px;
      flex: 1;
      align-items: flex-end;

      .default-background-btn {
        margin-left: 10px;
      }
    }

    .header-right {
      display: flex;
      gap: 10px;
      flex-shrink: 0;
    }

    .header-item {
      display: flex;
      align-items: center;
      gap: 10px;

      label {
        min-width: 80px;
        font-weight: 500;
      }
    }
  }

  .template-content {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;

    .section-title {
      font-size: 16px;
      font-weight: 500;
      margin-bottom: 15px;
      color: #303133;
    }

    .available-components {
      width: 150px;
      flex-shrink: 0;

      .component-item {
        padding: 8px 12px;
        margin-bottom: 8px;
        border: 1px solid #e4e7ed;
        border-radius: 4px;
        cursor: move;
        transition: all 0.3s;
        background: #fff;
        text-align: center;

        &:hover {
          border-color: #409eff;
          box-shadow: 0 2px 8px rgba(64, 158, 255, 0.2);
        }

        .component-name {
          font-size: 14px;
          font-weight: 500;
          color: #303133;
        }
      }
    }

    .preview-area {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;

      .preview-container {
        position: relative;
        border: 2px dashed #dcdfe6;
        border-radius: 4px;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        overflow: hidden;

        .preview-component {
          border: 2px solid transparent;
          user-select: none;

          &.active {
            border-color: #409eff;
            z-index: 10;
          }

          .component-content {
            display: inline-block;
            white-space: nowrap;
            // 文本组件自动适应内容，不使用固定宽高

            &.line-price {
              text-decoration: line-through;
            }

            &.qr-code-content {
              width: 100%;
              height: 100%;
              background: #f5f7fa;
              border-radius: 4px;
              display: flex;
              align-items: center;
              justify-content: center;

              .qr-code-placeholder {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;

                img {
                  width: 100%;
                  height: 100%;
                }
              }
            }
          }

          .component-controls {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            pointer-events: none;

            .delete-button {
              position: absolute;
              top: -12px;
              right: -12px;
              width: 24px;
              height: 24px;
              background: #f56c6c;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              pointer-events: all;
              color: #fff;
              font-size: 14px;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
              transition: all 0.3s;
              z-index: 20;

              &:hover {
                background: #f78989;
                transform: scale(1.1);
              }

              :deep(.el-icon) {
                font-size: 14px;
              }
            }
          }

          .resize-handles {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            pointer-events: none;

            .resize-handle {
              position: absolute;
              width: 8px;
              height: 8px;
              background: #409eff;
              border: 1px solid #fff;
              border-radius: 50%;
              pointer-events: all;
              cursor: nwse-resize;

              &.resize-handle-nw {
                top: -4px;
                left: -4px;
                cursor: nwse-resize;
              }

              &.resize-handle-ne {
                top: -4px;
                right: -4px;
                cursor: nesw-resize;
              }

              &.resize-handle-sw {
                bottom: -4px;
                left: -4px;
                cursor: nesw-resize;
              }

              &.resize-handle-se {
                bottom: -4px;
                right: -4px;
                cursor: nwse-resize;
              }
            }
          }
        }
      }
    }

    .component-properties {
      width: 140px;
      flex-shrink: 0;
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      padding: 10px;
      background: #fff;

      .empty-properties {
        color: #909399;
        text-align: center;
        padding: 40px 0;
      }

      .properties-content {
        .property-item {
          margin-bottom: 12px;

          label {
            display: block;
            margin-bottom: 6px;
            font-size: 13px;
            color: #606266;
          }

          &.delete-item {
            margin-bottom: 12px;
            padding-bottom: 12px;
            border-bottom: 1px solid #e4e7ed;
          }
        }
      }
    }
  }
}
</style>
